import { useState, useCallback, useRef } from 'react';

// Parcel data format: [ownerName, value, sqft, acreage, ownerOccupied (0/1), score]
const OWNER = 0;
const VALUE = 1;
const SQFT = 2;
const ACREAGE = 3;
const OCCUPIED = 4;
const SCORE = 5;

function normalizeAddress(address) {
  if (!address) return null;
  let addr = address.toUpperCase().trim();
  // Common abbreviations
  addr = addr.replace(/\bSTREET\b/g, 'ST');
  addr = addr.replace(/\bAVENUE\b/g, 'AVE');
  addr = addr.replace(/\bBOULEVARD\b/g, 'BLVD');
  addr = addr.replace(/\bDRIVE\b/g, 'DR');
  addr = addr.replace(/\bLANE\b/g, 'LN');
  addr = addr.replace(/\bCOURT\b/g, 'CT');
  addr = addr.replace(/\bCIRCLE\b/g, 'CIR');
  addr = addr.replace(/\bROAD\b/g, 'RD');
  addr = addr.replace(/\bPLACE\b/g, 'PL');
  addr = addr.replace(/\bTERRACE\b/g, 'TER');
  addr = addr.replace(/\bNORTH\b/g, 'N');
  addr = addr.replace(/\bSOUTH\b/g, 'S');
  addr = addr.replace(/\bEAST\b/g, 'E');
  addr = addr.replace(/\bWEST\b/g, 'W');
  addr = addr.replace(/[.,#]/g, '');
  addr = addr.replace(/\s+/g, ' ');
  return addr.trim();
}

export function useParcelData() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const parcelDataRef = useRef(null);
  const hotspotsRef = useRef(null);

  // Load parcel data lazily
  const loadParcelData = useCallback(async () => {
    if (parcelDataRef.current || isLoading) return parcelDataRef.current;

    setIsLoading(true);
    try {
      const [parcelRes, hotspotRes] = await Promise.all([
        fetch('/parcel_lookup.json'),
        fetch('/hotspots.json')
      ]);

      if (!parcelRes.ok) throw new Error('Failed to load parcel data');

      parcelDataRef.current = await parcelRes.json();
      hotspotsRef.current = await hotspotRes.json();
      setIsLoaded(true);
      setIsLoading(false);
      return parcelDataRef.current;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return null;
    }
  }, [isLoading]);

  // Enrich a lead with parcel data
  const enrichLead = useCallback((lead) => {
    if (!parcelDataRef.current || !lead.streetAddress) return lead;

    const key = normalizeAddress(lead.streetAddress);
    const parcel = parcelDataRef.current[key];

    if (!parcel) return { ...lead, parcelMatch: false };

    const ownerOccupied = parcel[OCCUPIED] === 1;
    const sqft = parcel[SQFT];
    const value = parcel[VALUE];
    const score = parcel[SCORE];

    // Estimate roof size (typically 1.3-1.5x floor sqft for pitched roof)
    const estRoofSqft = Math.round(sqft * 1.4);

    // Estimate roof replacement cost ($4-8 per sqft installed)
    const estRoofCostLow = estRoofSqft * 4;
    const estRoofCostHigh = estRoofSqft * 8;

    // Lead quality for roofers
    let leadQuality = 'standard';
    if (score >= 70 && ownerOccupied) leadQuality = 'hot';
    else if (score >= 50 && ownerOccupied) leadQuality = 'warm';
    else if (score >= 30) leadQuality = 'cool';

    return {
      ...lead,
      parcelMatch: true,
      ownerName: parcel[OWNER],
      assessedValue: value,
      sqft: sqft,
      acreage: parcel[ACREAGE],
      ownerOccupied,
      score,
      estRoofSqft,
      estRoofCostLow,
      estRoofCostHigh,
      leadQuality
    };
  }, []);

  // Enrich multiple leads
  const enrichLeads = useCallback(async (leads) => {
    await loadParcelData();
    return leads.map(enrichLead);
  }, [loadParcelData, enrichLead]);

  // Get hotspots for map
  const getHotspots = useCallback(() => {
    return hotspotsRef.current || [];
  }, []);

  return {
    isLoaded,
    isLoading,
    error,
    loadParcelData,
    enrichLead,
    enrichLeads,
    getHotspots,
    hotspots: hotspotsRef.current
  };
}

export default useParcelData;
